/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderUserContext,
  BuilderUserData,
  FilterNames,
  FilterRestService,
} from '@gh/builder-shared';
import { BuilderPermission, SystemRoleBit, UserAllowAccess } from '@gh/builder-shared-data';
import { AppRouter, DictionaryItem, getRouteInitData } from '@gh/common-shared';
import {
  observableFilterTracker,
  observablePlainFilterTracker,
  RouteDataService,
} from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { newGridPaginationCursor } from '@gh/core-ui-grid';
import { of$ } from '@gh/rx';
import { auditTime$, map$, skip$ } from '@gh/rx/operators';

import { concat, filter } from 'lodash';
import { Observable } from 'rxjs';

import { LeadBoardBusinessService } from '../../resources/lead-board-business.service';
import {
  ALL_SALESPERSON_SELECT_VALUE,
  isSalespersonSelectItemInactive,
  LEAD_BOARD_MOBILE_FILTER_INIT,
  LEAD_BOARD_SCROLL_INIT,
  LeadBoardScroll,
  SalespersonSelectItem,
} from '../../resources/lead-board.model';
import { LeadBoardCommandService } from './lead-board-command.service';
import { LeadBoardFormState } from './lead-board-form.service';
import { MatSelectChange } from '@angular/material/select';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { getBool } from '@gh/core-util';

@Component({
  standalone: false,
  selector: 'gh-lead-board',
  templateUrl: 'lead-board.component.html',
  styleUrls: ['lead-board.component.scss'],
  providers: [
    LeadBoardFormState,
    LeadBoardCommandService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadBoardComponent implements OnInit {

  priorities: DictionaryItem<any>[];
  stages: DictionaryItem<any>[];
  cursor = newGridPaginationCursor();
  salespersonList$: Observable<SalespersonSelectItem[]>;
  salespersonList: SalespersonSelectItem[];
  defaultSalespersonSelectValue = ALL_SALESPERSON_SELECT_VALUE;
  searchString = '';
  isSalespersonListExist: boolean = true;

  constructor(public form: LeadBoardFormState,
              private route: ActivatedRoute,
              private router: AppRouter,
              private routeData: RouteDataService,
              private filterService: FilterRestService,
              private businessService: LeadBoardBusinessService,
              private translationService: TranslationService,
              private commandService: LeadBoardCommandService,
              private userContext: BuilderUserContext) {
  }

  ngOnInit(): void {
    const {
      filter: initFilter, leadBoardList, priorities, stages, salespersonList, searchString, salesPersonId,
    } = getRouteInitData(this.route);
    this.searchString = searchString;
    this.defaultSalespersonSelectValue = salesPersonId || initFilter.salesPersonId || ALL_SALESPERSON_SELECT_VALUE;
    const { id: currentUserId, fullName: name, allowAccessToLeads } = <BuilderUserData>this.userContext.data;
    const salespersonOwnSelectList = [{ id: currentUserId, name }];
    this.commandService.buildForm(
      this.isSalespersonOwnLeads() ? currentUserId : salesPersonId || initFilter.salesPersonId,
      this.isSalespersonOwnLeads() ? salespersonOwnSelectList : salespersonList,
      leadBoardList,
      priorities,
      stages,
      this.isEditMode(),
      initFilter);

    this.priorities = priorities;
    this.stages = stages;
    this.salespersonList = salespersonList;
    if (this.userContext.hasSystemRole(SystemRoleBit.SalesPerson)
      && allowAccessToLeads === UserAllowAccess.OwnLeads) {
      this.defaultSalespersonSelectValue = currentUserId; // select current user as Salesperson
      // GJGSAM-335
      // this.form.setSalespersonId(currentUserId);
      this.isSalespersonListExist = false;
      this.salespersonList$ = of$(salespersonOwnSelectList);
    } else if (this.userContext.hasSystemRole(SystemRoleBit.SalesPerson)
      && allowAccessToLeads === UserAllowAccess.SalespersonLeads) {
      this.salespersonList$ = this.form.salespersonList$.pipe(
        map$((selectList: SalespersonSelectItem[]) =>
          filter(selectList, ({ id: value }) => value !== currentUserId)),
        map$((selectList: SalespersonSelectItem[]) => concat(salespersonOwnSelectList, selectList)));
    } else {
      this.salespersonList$ = this.form.salespersonList$;
    }

    const tracker = this.routeData
      .tracker(observablePlainFilterTracker({
        names: ['salesPersonId', 'showCompleted', 'showActive', 'showInactive', 'showOverdue', 'showSuppressed'],
        filterChanges: this.form.filter$,
        setFilter: (_filter: any) => {
          const boolFields = ['showCompleted', 'showActive', 'showInactive', 'showOverdue', 'showSuppressed'];
          const result: any = {};
          for (const key in _filter) {
            if (_filter[key] !== undefined) {
              result[key] = boolFields.includes(key) ? getBool(_filter[key]) : _filter[key];
            }
          }
          return this.commandService.setFilter(result);
        },
      }).withDefault(this.form.value.info))
      .withEffects(
        observableFilterTracker({
          name: 'mobileFilter',
          filterChanges: this.form.mobileFilter$,
          setFilter: (_filter) => this.commandService.setMobileFilter(_filter),
        }).withDefault(LEAD_BOARD_MOBILE_FILTER_INIT),
        observableFilterTracker({
          name: 'scroll',
          filterChanges: this.form.scroll$.pipe(auditTime$(1000)),
          setFilter: (scroll: LeadBoardScroll) => this.commandService.setScroll(scroll),
        }).withDefault(LEAD_BOARD_SCROLL_INIT),
        observableFilterTracker({
          name: 'opened',
          filterChanges: this.form.openedId$,
          setFilter: (id) => {
            if (id) {
              this.commandService.toggleCard(id);
            }
          },
        }));
    tracker.listen(this.route);
    tracker.filterChanges.pipe(
      skip$(1))
      .subscribe(([leadBordFilter]) => {
        this.filterService.update(FilterNames.leadsBord, leadBordFilter).subscribe();
        this.commandService.filterLeadBoardCardsByInfo();
      });
  }

  onSalespersonInfoChange({ value }: MatSelectChange): void {
    if (value === ALL_SALESPERSON_SELECT_VALUE) {
      this.commandService.setSalespersonId(void 0);
    } else {
      this.commandService.setSalespersonId(value);
    }
  }

  onSearchStringChange(value: string): void {
    this.searchString = value;
    this.commandService.setSearchString(value);
  }

  onInfoShowCompletedFilterChange({ checked }: any): void {
    this.commandService.setInfoShowCompletedFilter(checked);
  }

  onInfoShowActiveFilterChange({checked}: any): void {
    this.commandService.setInfoShowActiveFilter(checked);
  }

  onInfoShowInactiveFilterChange({ checked }: any): void {
    this.commandService.setInfoShowInactiveFilter(checked);
  }

  onInfoShowOverdueFilterChange({ checked }: MatSlideToggleChange): void {
    this.commandService.setInfoShowOverdueFilter(checked);
  }

  isSalespersonInactive(salesperson: SalespersonSelectItem): boolean {
    return isSalespersonSelectItemInactive(salesperson);
  }

  onCreate(): void {
    this.router.navigateToChild(['/sales/leads/new'], {
      parentTitle: 'lbl_leads',
    });
  }

  reloadInfo(): void {
    this.commandService.filterLeadBoardCardsByInfo();
  }

  private isEditMode(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT);
  }

  private isSalespersonOwnLeads(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.SalesPerson)
      && (<BuilderUserData>this.userContext.data).allowAccessToLeads === UserAllowAccess.OwnLeads;
  }
}
